import { AxiosResponse } from 'axios';
import axios from '../helpers/axios';
import { logError } from '../services';
import qs from 'querystring';
import { IDropdownResponse, INeighborhoodRes, INeighborhoodVendors, IResponse } from '../models';
import {
  INeighborhoodDetail,
  INeighborhoodDetailGeneralInfoPut,
  INeighborhoodDetailHoaInfoPut,
  INeighborhoodDetailSalesAndContractsPut,
  INeighborhoodDetailHouseSpecs,
  INeighborhoodDetailSalesAndContractsInfo,
  INeighborhoodDetailHoaInfo,
  INeighborhoodDetailHouseSpecsPut,
  INeighborhoodDetailHoaInfoImage,
  INeighborhoodOption,
  INeighborhoodAmenity,
  INeighborhoodAmenityDoc,
  INeighborhoodProjectManagers,
  INeighborhoodSupervisors,
  ISeries,
  IStandardFeaturesOptions,
  IBudgetOption,
  ISelectedBudgetOption,
  IExclusions,
  ISelectedExclusions,
  IExclusionPayload,
  IColorExclusions,
  INeighborhoodJob,
  INeighborhoodLotPlan,
  INeighborhoodDetailDescription,
  INeighborhoodAll,
  ILot,
  IAvailableNeighborhoodsWarrantyUser,
  IAvailableNeighborhoodsForemanUser
} from './../models/neighborhoods';

const base = `/api/neighborhoods`;

export const getNeighborhoods = async (
  filters: {
    territoryId?: number;
    page?: number;
    perPage?: number | string;
    sortBy?: string;
    sortDirection?: string;
    isActive?: boolean;
    hasActiveJobs?: boolean;
    hasActiveBuilds?: boolean;
    hasAvailableLots?: boolean;
    name?: string;
    code?: string | number;
    search?: string;
    hasSpecs?: boolean;
    hasPunchLists?: boolean;
    includeGeneralJob?: boolean;
    underContractBuildsOnly?: boolean;
  } = { sortBy: 'Name' }
): Promise<INeighborhoodRes> => {
  try {
    const { data } = await axios.get(`${base}`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoods failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodGeneralInfo = async (
  neighborhoodId: number | string
): Promise<INeighborhoodDetail> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/general-information`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodDetails = async (
  neighborhoodId: number | string
): Promise<INeighborhoodDetailDescription> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/details`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodDetails failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodBuildTypes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/build-types`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodBuildTypes failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createNeighborhoodGeneralInfo = async (
  payload: INeighborhoodDetailGeneralInfoPut
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base}/`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'createNeighborhoodGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putNeighborhoodGeneralInfo = async (
  neighborhoodId: number | string,
  payload: INeighborhoodDetailGeneralInfoPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`${base}/${neighborhoodId}/general-information`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putNeighborhoodGeneralInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodSalesAndContractInfo = async (
  neighborhoodId: number | string
): Promise<INeighborhoodDetailSalesAndContractsInfo> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/sales-and-contracts`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodSalesAndContractInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putNeighborhoodSalesAndContractInfo = async (
  neighborhoodId: number | string,
  payload: INeighborhoodDetailSalesAndContractsPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`${base}/${neighborhoodId}/sales-and-contracts`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putNeighborhoodSalesAndContractInfo failed');
    return Promise.reject(error?.response?.data);
  }
};
export const getNeighborhoodHoaInfo = async (
  neighborhoodId: number | string
): Promise<INeighborhoodDetailHoaInfo> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/hoa-information`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodHoaInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const putNeighborhoodHoaInfo = async (
  neighborhoodId: number | string,
  payload: INeighborhoodDetailHoaInfoPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`${base}/${neighborhoodId}/hoa-information`, payload);
    return data;
  } catch (error) {
    logError({ error, payload: payload }, 'putNeighborhoodHoaInfo failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodLots = async (filters: {
  neighborhoodId?: number | string;
  isSpec?: boolean;
  includeGeneralJob?: boolean;
}): Promise<ILot[]> => {
  try {
    const { data } = await axios.get(`${base}/lots`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodLots failed');
    return Promise.reject(error);
  }
};

export const getNeighborhoodNames = async (): Promise<IResponse<{ name: string }[]>> => {
  try {
    const { data } = await axios.get(`${base}/unique-neighborhoods`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodNames failed');
    return Promise.reject(error);
  }
};

export const getNeighborhoodVendors = async (
  neighborhoodId: number | string,
  filters?: {
    status?: 'New' | 'Approved' | 'Remediating' | 'Placed' | 'Delivered' | 'Cancelled' | string;
    sortBy?: 'Id' | 'Date' | 'Neighborhood' | 'LotNumber' | 'OrderPlacer' | 'Status' | string;
    sortDirection?: 'Asc' | 'Desc' | string;
    page?: number;
    perPage?: number;
    lotNumber?: number | string | null;
    isReturnPO?: boolean;
  }
): Promise<INeighborhoodVendors> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/vendors`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodVendors failed');
    return Promise.reject(error);
  }
};

export const getSitePrepBudget = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/site-prep-budget-levels`);
    return data;
  } catch (error) {
    logError(error, 'getSitePrepBudget failed');
    return Promise.reject(error);
  }
};

export const getActivationNotice = async neighborhoodId => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/activation-notice`);
    return data;
  } catch (error) {
    logError(error, 'getActivationNotice failed');
    return Promise.reject(error);
  }
};

export const postActivationNotice = async (neighborhoodId, activationNoticeData) => {
  try {
    const { data } = await axios.post(
      `${base}/${neighborhoodId}/activation-notice`,
      activationNoticeData
    );
    return data;
  } catch (error) {
    logError(
      { error, payload: { neighborhoodId, activationNoticeData } },
      'postActivationNotice failed'
    );
    return Promise.reject(error);
  }
};

export const getNeighborhoodActivationPacketDocs = async neighborhoodId => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/activation-packet/document-types`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodActivationPacketDocs failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodActivationPacketDocFromUrl = async url => {
  try {
    return await axios.get(url, {
      responseType: 'blob'
    });
  } catch (error) {
    logError(error, 'getNeighborhoodActivationPacketDocFromUrl failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getHoaPaymentFrequencyOptions = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/hoa-payment-frequencies`);
    return data;
  } catch (error) {
    logError(error, 'getHoaPaymentFrequency failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getHoaStreetOwnerOptions = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/street-owners`);
    return data;
  } catch (error) {
    logError(error, 'getHoaStreetOwnerOptions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const postNeighborhoodHoaFiles = async (
  neighborhoodId: number | string,
  documentUploadOwner: string,
  imagePost: FormData
): Promise<INeighborhoodDetailHoaInfoImage[]> => {
  try {
    const { data } = await axios.post(
      `${base}/${neighborhoodId}/documents/${documentUploadOwner}`,
      imagePost
    );
    return data;
  } catch (error) {
    logError(
      { error, payload: { neighborhoodId, documentUploadOwner, imagePost } },
      'postNeighborhoodHoaFiles failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodHouseSpecs = async (
  neighborhoodId: string
): Promise<INeighborhoodDetailHouseSpecs> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/house-specifications`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodHouseSpecs failed');
    return Promise.reject(error?.response?.data);
  }
};

export const updateNeighborhoodHouseSpecs = async (
  neighborhoodId: string,
  payload: INeighborhoodDetailHouseSpecsPut
): Promise<any> => {
  try {
    const { data } = await axios.put(`${base}/${neighborhoodId}/house-specifications`, payload);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodHouseSpecs failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodHouseSpecsColorSchemes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/color-scheme-areas`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodHouseSpecsColorSchemes failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodHouseSpecsPlanFinishLevels = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/plan-finish-levels`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodHouseSpecsPlanFinishLevels failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodOptions = async (
  neighborhoodId: number | string,
  filters?: {
    page?: number;
    perPage?: number | string;
    sortBy?: string;
    sortDirection?: 'Name' | 'PlanStyle';
  }
): Promise<IResponse<INeighborhoodOption[]>> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/options`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodOptions failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createNeighborhoodCopySeries = async (payload: {
  sourceNeighborhoodId: number;
  destinationNeighborhoodId: number;
}): Promise<IResponse<INeighborhoodOption[]>> => {
  try {
    const { data } = await axios.post(`${base}/copy-series`, payload);
    return data;
  } catch (error) {
    logError(error, 'createNeighborhoodCopySeries failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getSingleNeighborhoodAmenities = async (
  neighborhoodId: number | string,
  filters?: {
    page?: number;
    perPage?: number | string;
    sortBy?: string;
    sortDirection?: string;
  }
): Promise<IResponse<INeighborhoodAmenity[]>> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/amenities`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodAmenities failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createNeighborhoodAmenity = async (
  neighborhoodId: string | number,
  payload: {
    amenities: string[];
  }
): Promise<IResponse<INeighborhoodOption[]>> => {
  try {
    const { data } = await axios.post(`${base}/${neighborhoodId}/amenities`, payload);
    return data;
  } catch (error) {
    logError(error, 'createNeighborhoodAmenity failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createNeighborhoodAmenityImages = async (
  neighborhoodId: string | number,
  amenityId: number,
  imagePost: FormData
): Promise<INeighborhoodAmenityDoc[]> => {
  try {
    const { data } = await axios.put(`${base}/${neighborhoodId}/amenities/${amenityId}`, imagePost);
    return data;
  } catch (error: any) {
    logError(error, 'createNeighborhoodAmenityImages failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodAmenityImages = async (
  neighborhoodId: string | number,
  photoId: number
): Promise<string[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/amenities/photos/${photoId}`);
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodAmenityImages failed');
    return Promise.reject(error?.response?.data);
  }
};

export const removeNeighborhoodAmenity = async (
  neighborhoodId: string | number,
  amenityId: number
): Promise<string[]> => {
  try {
    const { data } = await axios.delete(`${base}/${neighborhoodId}/amenities/${amenityId}`);
    return data;
  } catch (error: any) {
    logError(error, 'removeNeighborhoodAmenity failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getNeighborhoodAmenities = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/amenities`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodAmenitites failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodProjectManagers = async (
  neighborhoodId: string | number
): Promise<INeighborhoodProjectManagers[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/project-managers`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodProjectManagers failed');
    return Promise.reject(error.response.data);
  }
};

export const getAvailableProjectManagers = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/project-managers/lookup`);
    return data;
  } catch (error) {
    logError(error, 'getAvailableProjectManagers failed');
    return Promise.reject(error.response.data);
  }
};

export const getProjectManagerNeighborhoodNames = async (
  projectManagerId: string | number
): Promise<string[]> => {
  try {
    const { data } = await axios.get(
      `/api/project-managers/${projectManagerId}/neighborhood-names`
    );
    return data;
  } catch (error) {
    logError(error, 'getProjectManagerNeighborhoodNames failed');
    return Promise.reject(error.response.data);
  }
};

export const addProjectManagerToNeighborhood = async (
  neighborhoodId: string | number,
  projectManagerId: string | number,
  priorityOrder?: string | number
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base}/${neighborhoodId}/project-managers`, {
      projectManagerId,
      priorityOrder
    });
    return data;
  } catch (error: any) {
    logError(error, 'addProjectManagerToNeighborhood failed');
    return Promise.reject(error.response.data);
  }
};

export const getAvailableSupervisors = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/supervisors/lookup`);
    return data;
  } catch (error) {
    logError(error, 'getAvailableSupervisors failed');
    return Promise.reject(error.response.data);
  }
};

export const getSupervisorNeighborhoodNames = async (
  supervisorId: string | number
): Promise<string[]> => {
  try {
    const { data } = await axios.get(`/api/supervisors/${supervisorId}/neighborhood-names`);
    return data;
  } catch (error) {
    logError(error, 'getSupervisorNeighborhoodNames failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSupervisors = async (
  neighborhoodId: string | number
): Promise<INeighborhoodSupervisors[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/supervisors`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodSupervisors failed');
    return Promise.reject(error.response.data);
  }
};

export const addSupervisorToNeighborhood = async (
  neighborhoodId: string | number,
  supervisorId: string | number,
  priorityOrder?: string | number
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base}/${neighborhoodId}/supervisors`, {
      supervisorId,
      priorityOrder
    });
    return data;
  } catch (error: any) {
    logError(error, 'addSupervisorToNeighborhood failed');
    return Promise.reject(error.response.data);
  }
};

export const getAvailableWarrantyUsers = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/warranty-users/lookup`);
    return data;
  } catch (error) {
    logError(error, 'getAvailableWarrantyUsers failed');
    return Promise.reject(error.response.data);
  }
};

export const getWarrantyUserNeighborhoodNames = async (
  warrantyUserId: string | number
): Promise<string[]> => {
  try {
    const { data } = await axios.get(`/api/warranty-users/${warrantyUserId}/neighborhood-names`);
    return data;
  } catch (error) {
    logError(error, 'getWarrantyUserNeighborhoodNames failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodWarrantyUsers = async (
  neighborhoodId: string | number
): Promise<IAvailableNeighborhoodsWarrantyUser[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/warranty-users`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodsWarrantyUsers failed');
    return Promise.reject(error.response.data);
  }
};

export const addWarrantyUserToNeighborhood = async (
  neighborhoodId: string | number,
  warrantyUserId: string | number,
  priorityOrder?: string | number
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base}/${neighborhoodId}/warranty-users`, {
      warrantyUserId,
      priorityOrder
    });
    return data;
  } catch (error: any) {
    logError(error, 'addWarrantyUserToNeighborhood failed');
    return Promise.reject(error.response.data);
  }
};

export const deleteWarrantyUserFromNeighborhood = async (
  warrantyUserId: string | number,
  neighborhoodId: string | number
): Promise<any> => {
  try {
    const { data } = await axios.delete(
      `/api/warranty-users/${warrantyUserId}/neighborhoods/${neighborhoodId}`
    );
    return data;
  } catch (error: any) {
    logError(error, 'deleteWarrantyUserFromNeighborhood failed');
    return Promise.reject(error.response.data);
  }
};

// export const deleteSupervisorNeighborhood = async (
//   supervisorId: string | number,
//   neighborhoodId: string | number
// ): Promise<any> => {
//   try {
//     const { data } = await axios.delete(
//       `/api/supervisors/${supervisorId}/neighborhoods/${neighborhoodId}`
//     );
//     return data;
//   } catch (error: any) {
//     logError(error, 'deleteSupervisorNeighborhood failed');
//     return Promise.reject(error.response.data);
//   }
// };

export const getNeighborhoodSeriesGeneralInfo = async (
  neighborhoodId: string | number,
  seriesId: string | number
): Promise<ISeries> => {
  try {
    const { data } = await axios.get(
      `${base}/${neighborhoodId}/series/${seriesId}/general-information`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesGeneralInfo failed');
    return Promise.reject(error.response.data);
  }
};

export const createNeighborhoodSeriesGeneralInfo = async (
  neighborhoodId: string | number,
  payload: ISeries
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${base}/${neighborhoodId}/series/general-information`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'createNeighborhoodSeriesGeneralInfo failed');
    return Promise.reject(error.response.data);
  }
};

export const updateNeighborhoodSeriesGeneralInfo = async (
  neighborhoodId: string | number,
  seriesId: string | number,
  payload: ISeries
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `${base}/${neighborhoodId}/series/${seriesId}/general-information`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'createNeighborhoodSeriesGeneralInfo failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesStandardFeaturesOptions =
  async (): Promise<IStandardFeaturesOptions> => {
    try {
      const { data } = await axios.get(`${base}/series/standard-features`);
      return data;
    } catch (error: any) {
      logError(error, 'getNeighborhoodSeriesStandardFeaturesOptions failed');
      return Promise.reject(error.response.data);
    }
  };

export const getNeighborhoodSeriesStandardFeatures = async (
  neighborhoodId: string | number,
  seriesId: string | number
): Promise<number[]> => {
  try {
    const { data } = await axios.get(
      `${base}/${neighborhoodId}/series/${seriesId}/standard-features`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const updateNeighborhoodSeriesStandardFeatures = async (
  neighborhoodId: string | number,
  seriesId: string | number,
  payload: number[]
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `${base}/${neighborhoodId}/series/${seriesId}/standard-features`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'updateNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesBudgetOptions = async (): Promise<IBudgetOption[]> => {
  try {
    const { data } = await axios.get(`${base}/series/budget-options`);
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesStandardFeaturesOptions failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesSelectedBudgetOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number
): Promise<ISelectedBudgetOption[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/series/${seriesId}/budget-options`);
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const updateNeighborhoodSeriesSelectedBudgetOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number,
  payload: ISelectedBudgetOption[]
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `${base}/${neighborhoodId}/series/${seriesId}/budget-options`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'updateNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesExclusionOptions = async (): Promise<IExclusions> => {
  try {
    const { data } = await axios.get(`${base}/series/exclusion-options`);
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesStandardFeaturesOptions failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesSelectedExclusionOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number
): Promise<ISelectedExclusions> => {
  try {
    const { data } = await axios.get(
      `${base}/${neighborhoodId}/series/${seriesId}/exclusion-options`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const createNeighborhoodSeriesSelectedExclusionOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number,
  payload: IExclusionPayload
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${base}/${neighborhoodId}/series/${seriesId}/exclusion-options`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'updateNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesColorExclusionOptions = async (): Promise<IColorExclusions[]> => {
  try {
    const { data } = await axios.get(`${base}/series/color-exclusion-options`);
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesColorExclusionOptions failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodSeriesSelectedColorExclusionOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number
): Promise<number[]> => {
  try {
    const { data } = await axios.get(
      `${base}/${neighborhoodId}/series/${seriesId}/color-exclusion-options`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const updateNeighborhoodSeriesSelectedColorExclusionOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number,
  payload: number[]
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `${base}/${neighborhoodId}/series/${seriesId}/color-exclusion-options`,
      payload
    );
    return data;
  } catch (error: any) {
    logError(error, 'updateNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const removeNeighborhoodSeriesSelectedExclusionOptions = async (
  neighborhoodId: string | number,
  seriesId: string | number,
  params: IExclusionPayload
): Promise<any> => {
  try {
    const { data } = await axios.delete(
      `${base}/${neighborhoodId}/series/${seriesId}/exclusion-options`,
      {
        params: {
          'ExclusionOptionInfo.OptionName': params.optionName ?? null,
          'ExclusionOptionInfo.OptionSetId': params.optionSetId ?? null,
          'ExclusionOptionInfo.SageID': params.sageID ?? null
        },
        paramsSerializer: params => qs.stringify(params)
      }
    );
    return data;
  } catch (error: any) {
    logError(error, 'updateNeighborhoodSeriesStandardFeatures failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodPlanStyles = async (
  seriesId: string | number
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/plan-styles`, {
      params: { seriesId },
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodPlanStyles failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodOptionLines = async (
  seriesId: string | number
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/option-lines`, {
      params: { seriesId },
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodOptionLines failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodPlans = async (
  seriesId: string | number
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${base}/plans`, {
      params: { seriesId },
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodPlans failed');
    return Promise.reject(error.response.data);
  }
};

export const getAddendumA = async (
  neighborhoodId: string | number,
  seriesId: string | number
): Promise<AxiosResponse> => {
  try {
    const data = await axios.get(`${base}/${neighborhoodId}/series/${seriesId}/addendum-a`, {
      responseType: 'blob'
    });
    return data;
  } catch (error: any) {
    logError(error, 'getAddendumA failed');
    return Promise.reject(error.response.data);
  }
};

export const getPhotoList = async (
  neighborhoodId: string | number,
  photoOwnerType: string
): Promise<ISelectedExclusions> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/list-photos/${photoOwnerType}`);
    return data;
  } catch (error: any) {
    logError(error, 'getPhotoList failed');
    return Promise.reject(error.response.data);
  }
};

export const createPhoto = async (
  neighborhoodId: string | number,
  photoOwnerType: string,
  imagePost: FormData
): Promise<string[]> => {
  try {
    const { data } = await axios.post(
      `${base}/${neighborhoodId}/photos/${photoOwnerType}`,
      imagePost
    );
    return data;
  } catch (error) {
    logError({ error, payload: imagePost }, 'createPhoto failed');
    return Promise.reject(error?.response?.data);
  }
};
export const getNeighborhoodPriceGuide = async (
  neighborhoodId: number | string,
  use3CarPricing?: boolean
): Promise<any> => {
  try {
    return await axios.get(`${base}/${neighborhoodId}/price-guide`, {
      params: { use3CarPricing },
      paramsSerializer: params => qs.stringify(params),
      responseType: 'blob'
    });
  } catch (error: any) {
    logError(error, 'getNeighborhoodPriceGuide failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodJob = async (filters: {
  neighborhoodId?: number | string;
  lotNumber?: number | string;
}): Promise<INeighborhoodJob> => {
  try {
    const { data } = await axios.get(`${base}/job`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodJob failed');
    return Promise.reject(error);
  }
};

export const getNeighborhoodLotPlans = async (
  neighborhoodId: string | number,
  lotNumber: string | number
): Promise<INeighborhoodLotPlan[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/lots/${lotNumber}/plans`);
    return data;
  } catch (error: any) {
    logError(error, 'getNeighborhoodLotPlans failed');
    return Promise.reject(error.response.data);
  }
};

export const getAllNeighborhoodsAndLots = async (filters: {
  isActive?: boolean;
  hasActiveJobs?: boolean;
}): Promise<INeighborhoodAll[]> => {
  try {
    const { data } = await axios.get(`${base}/all`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError(error, 'getAllNeighborhoodsAndLots failed');
    return Promise.reject(error.response.data);
  }
};

export const getNeighborhoodUpgradePricingReport = async (
  neighborhoodId: number | string,
  lotNumber: string | number,
  filters?: {
    neighborhoodId: number | string;
    lotNumber: string | number;
    planId?: string | number;
  }
): Promise<any> => {
  try {
    return await axios.get(`${base}/${neighborhoodId}/lots/${lotNumber}/upgrade-pricing-report`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params),
      responseType: 'blob'
    });
  } catch (error: any) {
    let responseObj = await error.response.data.text();
    logError(error, 'getNeighborhoodUpgradePricingReport failed');
    return Promise.reject(JSON.parse(responseObj));
  }
};

export const getNeighborhoodLotDetails = async (neighborhoodId: number | string): Promise<any> => {
  try {
    return await axios.get(`${base}/${neighborhoodId}/lot-details-report`, {
      responseType: 'blob'
    });
  } catch (error: any) {
    let responseObj = await error.response.data.text();
    logError(error, 'getNeighborhoodLotDetails failed');
    return Promise.reject(JSON.parse(responseObj));
  }
};

export const getNeighborhoodForemanUsers = async (
  neighborhoodId: string | number
): Promise<IAvailableNeighborhoodsForemanUser[]> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/foreman-users`);
    return data;
  } catch (error) {
    logError(error, 'getNeighborhoodForemanUsers failed');
    return Promise.reject(error.response.data);
  }
};

export const addForemanToNeighborhood = async (
  neighborhoodId: string | number,
  foremanUserId: string | number,
  priorityOrder?: string | number
): Promise<any> => {
  try {
    const { data } = await axios.post(`${base}/${neighborhoodId}/foreman-users`, {
      foremanUserId,
      priorityOrder
    });
    return data;
  } catch (error: any) {
    logError(error, 'addForemanToNeighborhood failed');
    return Promise.reject(error.response.data);
  }
};

export const doesInProgressBuildExist = async (
  neighborhoodId: string | number,
  lotNumber: string | number
): Promise<any> => {
  try {
    const { data } = await axios.get(`${base}/${neighborhoodId}/lots/${lotNumber}/check-existing`);
    return data;
  } catch (error) {
    logError(error, 'doesInProgressBuildExist failed');
    return Promise.reject(error.response.data);
  }
};
